import React, { ReactNode, useState } from "react";
import _ from "lodash";
import {
  Button,
  createStyles,
  DialogActions,
  DialogContent,
  Grid,
  makeStyles,
  Typography,
  useTheme,
} from "@material-ui/core";
import "jspdf-autotable";
import WebAssetIcon from "@material-ui/icons/WebAsset";

import { CustomTheme } from "../../../../types/customTheme";
import DialogCommonDefault from "../../../../components/dialogs/DialogCommonDefault";
import ButtonCommon from "../../../../components/buttons/ButtonCommon";
import DefaultAlert from "../../../../components/alerts/DefaultAlert";

export interface DashboardCreateModalProps {
  open: any;
  setOpen: any;
  allWidgets: any;
  selectedWidgets: any;
  setSelectedWidgets: any;
  layout: any;
  setLayout: any;
  stationNode: any;
  cols?: number;
}

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    autoComplete: {
      marginTop: "4px",
      marginLeft: "12px",
      "& .MuiAutocomplete-input": {
        color: theme.palette.custom.orange.contrastText,
      },
      "& .MuiInputBase-root": {
        color: "inherit",
        backgroundColor: theme.palette.background.entity_background,
        borderRadius: 10,
      },
      "& fieldset": {
        borderRadius: 10,
        border: "none",
        color: theme.palette.custom.orange.contrastText,
      },
    },
  }),
);

const displayText = [
  "Title Box",
  "Text Box",
  "Heading",
  "Title Only Box",
  "Text Only Box",
  "Heading Only Box",
];

const WidgetSelectModal: React.FunctionComponent<DashboardCreateModalProps> = ({
  open,
  setOpen,
  allWidgets,
  layout,
  selectedWidgets,
  setSelectedWidgets,
  setLayout,
  stationNode,
}) => {
  const [isOpenSelectionWidgetModal, setIsOpenSelectionWidgetModal] =
    useState(false);
  const [isOpenTextElementModal, setIsOpenTextElementModal] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleOpenSelectedModal = () => {
    setIsOpenSelectionWidgetModal(true);
  };

  const handleOpenTextElementModal = () => {
    setIsOpenTextElementModal(true);
  };

  const handleOpenSpacerElementModal = () => {
    handleSelectEmptyWidget();
  };

  function generateKey(length = 8) {
    const charset =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let key = "";
    for (let i = 0; i < length; i++) {
      key += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return key;
  }

  const COLS = 12; // your grid column count

  const handleSelectWidget = (widget: any) => {
    const firstCode: any = Object.values(stationNode)[0];
    if (widget.path[0] === "stationAverageTime" && firstCode?.code && Array.isArray(widget.path)) {
      widget.path[1] = firstCode?.code;
      widget.label = firstCode?.name;
      widget.labelInitial = firstCode?.name;
    }
    const newKey = generateKey();
    const cloneWidget = _.cloneDeep(widget);
    const cloneSelectedWidgets = _.cloneDeep(selectedWidgets);
    const cloneLayout = _.cloneDeep(layout);

    cloneWidget["key"] = newKey;
    cloneSelectedWidgets.push(cloneWidget);
    setSelectedWidgets(cloneSelectedWidgets);

    // Step 1: Build a map of occupied cells: occupied[x][y] = true
    const occupied: { [x: number]: Set<number> } = {};
    for (let x = 0; x < COLS; x++) {
      occupied[x] = new Set<number>();
    }

    cloneLayout.forEach((item: any) => {
      for (let dx = 0; dx < item.w; dx++) {
        for (let dy = 0; dy < item.h; dy++) {
          const px = item.x + dx;
          const py = item.y + dy;
          if (!occupied[px]) occupied[px] = new Set<number>();
          occupied[px].add(py);
        }
      }
    });

    // Step 2: Find first available (x, y) slot
    let targetX = 0;
    let targetY = 0;
    let found = false;

    for (let y = 0; y < 100 && !found; y++) {
      // limit scan depth
      for (let x = 0; x < COLS && !found; x++) {
        if (!occupied[x].has(y)) {
          targetX = x;
          targetY = y;
          found = true;
        }
      }
    }

    // Step 3: Add new widget to found (x, y)
    const newLayoutItem = {
      i: newKey,
      x: targetX,
      y: targetY,
      w: 3,
      h: 1,
      data: cloneWidget,
    };
    cloneLayout.push(newLayoutItem);
    setLayout(cloneLayout);
    setIsOpenSelectionWidgetModal(false);
    setIsOpenTextElementModal(false);
    setOpen(false);
  };

  const handleSelectEmptyWidget = () => {
    const newKey = generateKey();
    const cloneWidget = _.cloneDeep({ key: "", main: "spacer" });
    const cloneSelectedWidgets = _.cloneDeep(selectedWidgets);
    const cloneLayout = _.cloneDeep(layout);

    cloneWidget["key"] = newKey;
    cloneSelectedWidgets.push(cloneWidget);
    setSelectedWidgets(cloneSelectedWidgets);

    // Step 1: Build a map of occupied cells: occupied[x][y] = true
    const occupied: { [x: number]: Set<number> } = {};
    for (let x = 0; x < COLS; x++) {
      occupied[x] = new Set<number>();
    }

    cloneLayout.forEach((item: any) => {
      for (let dx = 0; dx < item.w; dx++) {
        for (let dy = 0; dy < item.h; dy++) {
          const px = item.x + dx;
          const py = item.y + dy;
          if (!occupied[px]) occupied[px] = new Set<number>();
          occupied[px].add(py);
        }
      }
    });

    // Step 2: Find first available (x, y) slot
    let targetX = 0;
    let targetY = 0;
    let found = false;

    for (let y = 0; y < 100 && !found; y++) {
      // limit scan depth
      for (let x = 0; x < COLS && !found; x++) {
        if (!occupied[x].has(y)) {
          targetX = x;
          targetY = y;
          found = true;
        }
      }
    }

    // Step 3: Add new widget to found (x, y)
    const newLayoutItem = {
      i: newKey,
      x: targetX,
      y: targetY,
      w: 3,
      h: 1,
      data: cloneWidget,
    };

    cloneLayout.push(newLayoutItem);
    setLayout(cloneLayout);
    setIsOpenSelectionWidgetModal(false);
    setIsOpenTextElementModal(false);
    setOpen(false);
  };

  const handleSelectText = (text: any, headType: any) => {
    const newKey = generateKey();
    const cloneWidget: any = {
      main: headType,
      label: text,
      labelInitial: text,
    };
    const cloneSelectedWidgets = _.cloneDeep(selectedWidgets);
    const cloneLayout = _.cloneDeep(layout);

    cloneWidget["key"] = newKey;
    cloneSelectedWidgets.push(cloneWidget);
    setSelectedWidgets(cloneSelectedWidgets);

    // Step 1: Build a map of occupied cells: occupied[x][y] = true
    const occupied: { [x: number]: Set<number> } = {};
    for (let x = 0; x < COLS; x++) {
      occupied[x] = new Set<number>();
    }

    cloneLayout.forEach((item: any) => {
      for (let dx = 0; dx < item.w; dx++) {
        for (let dy = 0; dy < item.h; dy++) {
          const px = item.x + dx;
          const py = item.y + dy;
          if (!occupied[px]) occupied[px] = new Set<number>();
          occupied[px].add(py);
        }
      }
    });

    // Step 2: Find first available (x, y) slot
    let targetX = 0;
    let targetY = 0;
    let found = false;

    for (let y = 0; y < 100 && !found; y++) {
      // limit scan depth
      for (let x = 0; x < COLS && !found; x++) {
        if (!occupied[x].has(y)) {
          targetX = x;
          targetY = y;
          found = true;
        }
      }
    }

    // Step 3: Add new widget to found (x, y)
    const newLayoutItem = {
      i: newKey,
      x: targetX,
      y: targetY,
      w: 3,
      h: 1,
      data: cloneWidget,
    };

    cloneLayout.push(newLayoutItem);
    setLayout(cloneLayout);
    setIsOpenSelectionWidgetModal(false);
    setIsOpenTextElementModal(false);
    setOpen(false);
  };

  const grouped: any = {
    Core: allWidgets.filter((w: any) => w.main === "Core"),
    Channel: allWidgets.filter((w: any) => w.main === "Channel"),
    Mode: allWidgets.filter((w: any) => w.main === "Mode"),
    Platform: allWidgets.filter((w: any) => w.main === "Platform"),
    StationAverageTime: allWidgets.filter(
      (w: any) =>
        w.main !== "Core" &&
        w.main !== "Channel" &&
        w.main !== "Mode" &&
        w.main !== "Platform",
    ),
  };
  const displayOrder = [
    "Core",
    "Channel",
    "Mode",
    "Platform",
    // "StationAverageTime",
  ];

  const displayLabels: Record<string, string> = {
    Core: "Core",
    Channel: "Channel",
    Mode: "Mode",
    Platform: "Platform",
    // StationAverageTime: "Station Average Time",
  };

  const theme: CustomTheme = useTheme();
  const classes: any = useStyles();

  const handleDisplay = (): ReactNode => {
    if (!isOpenSelectionWidgetModal && !isOpenTextElementModal) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Button
              onClick={handleOpenSelectedModal}
              style={{
                width: "100%",
                backgroundColor: theme.palette.background.entity_background,
                borderRadius: "10px",
                height: "180px",
                textTransform: "none",
                border: `1px solid ${theme.palette.background.entity_border}`,
              }}
            >
              <div>
                <div>
                  <WebAssetIcon style={{ fontSize: "44px" }} />
                </div>
                <div>
                  <Typography variant="h6">Basic Element</Typography>
                </div>
              </div>
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              onClick={handleOpenTextElementModal}
              style={{
                width: "100%",
                backgroundColor: theme.palette.background.entity_background,
                borderRadius: "10px",
                height: "180px",
                textTransform: "none",
                border: `1px solid ${theme.palette.background.entity_border}`,
              }}
            >
              <div>
                <div>
                  <WebAssetIcon style={{ fontSize: "44px" }} />
                </div>
                <div>
                  <Typography variant="h6">Text Element</Typography>
                </div>
              </div>
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              onClick={handleOpenSpacerElementModal}
              style={{
                width: "100%",
                backgroundColor: theme.palette.background.entity_background,
                borderRadius: "10px",
                height: "180px",
                textTransform: "none",
                border: `1px solid ${theme.palette.background.entity_border}`,
              }}
            >
              <div>
                <div>
                  <WebAssetIcon style={{ fontSize: "44px" }} />
                </div>
                <div>
                  <Typography variant="h6">Spacer</Typography>
                </div>
              </div>
            </Button>
          </Grid>
        </Grid>
      );
    } else if (isOpenSelectionWidgetModal) {
      return (
        <Grid container spacing={2}>
          {displayOrder.map((groupKey) => (
            <React.Fragment key={groupKey}>
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  style={{ fontWeight: "bold", marginTop: 16 }}
                >
                  {displayLabels[groupKey]}
                </Typography>
              </Grid>
              {grouped[groupKey].map((item: any) => (
                <Grid item xs={4} key={item.key}>
                  <Button
                    onClick={() => handleSelectWidget(item)}
                    style={{
                      width: "100%",
                      backgroundColor:
                        theme.palette.background.entity_background,
                      borderRadius: "10px",
                      height: "120px",
                      textTransform: "none",
                      border: `1px solid ${theme.palette.background.entity_border}`,
                    }}
                  >
                    <Typography variant="body1">
                      {groupKey !== "StationAverageTime" && item.path[0] === "stationAverageTime"
                        ? "Station Average Time"
                        : item.label}
                    </Typography>
                  </Button>
                </Grid>
              ))}
            </React.Fragment>
          ))}
        </Grid>
      );
    } else {
      return (
        <Grid container spacing={2}>
          {displayText.map((text, index) => (
            <Grid item xs={4} key={text}>
              <Button
                onClick={() =>
                  handleSelectText(text, index > 2 ? "headNormal" : "headBox")
                }
                style={{
                  width: "100%",
                  backgroundColor: theme.palette.background.entity_background,
                  borderRadius: "10px",
                  height: "120px",
                  textTransform: "none",
                  border: `1px solid ${theme.palette.background.entity_border}`,
                }}
              >
                <Typography variant="body1">{text}</Typography>
              </Button>
            </Grid>
          ))}
        </Grid>
      );
    }
  };

  return (
    <>
      <DialogCommonDefault
        open={open}
        setOpen={setOpen}
        maxWidth={isOpenSelectionWidgetModal ? "lg" : "xs"}
        isNeedFixedHeight={false}
      >
        <DialogContent>{handleDisplay()}</DialogContent>
        <DialogActions style={{ marginBottom: "12px", marginRight: "12px" }}>
          <ButtonCommon
            style={{ width: "120px" }}
            onClick={() => {
              setOpen(false);
              setIsOpenSelectionWidgetModal(false);
              setIsOpenTextElementModal(false);
            }}
          >
            Cancel
          </ButtonCommon>
        </DialogActions>
      </DialogCommonDefault>
      <DefaultAlert
        open={!!success}
        handleClose={() => setSuccess("")}
        message={success}
        severity={"success"}
      />

      <DefaultAlert
        open={!!error}
        handleClose={() => setError("")}
        message={error}
        severity="error"
      />
    </>
  );
};

export default WidgetSelectModal;
